import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';

import { environment } from '../../environment/environment';
import { ConfigService } from './config.service';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { getApps } from 'firebase/app';
import { AppConfig } from '../models/config.interface';

export interface NotificationPayload {
  notification?: {
    title?: string;
    body?: string;
  };
  data: {
    order_id: string;
    [key: string]: unknown;
  };
}

export interface NotificationAction {
  orderId: string | null;
  path: string;
  new: boolean;
}

@Injectable({ providedIn: 'root' })
export class MessageService {
  public notification$ = new Subject<NotificationAction>();
  public notificationOrder$ = new Subject<Record<string, unknown>>();

  private messaging!: Messaging;
  private configService = inject(ConfigService);
  private platformId = inject(PLATFORM_ID);
  toastr = inject(ToastrService);
  router = inject(Router);
  audio!: HTMLAudioElement;
  show = false;
  title = '';
  body = '';
  route = '';
  timeoutId: ReturnType<typeof setTimeout> | undefined;

  notificationTitle: string | undefined;
  notificationBody: string | undefined;
  orderId: string | null = null;
  role: string | null = null;
  routePath: string | null = null;
  private swRegistration: ServiceWorkerRegistration | undefined;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.audio = new Audio();
      this.audio.src = '/voice/notification.wav';
      this.initiateAudio();

      // Ensure messaging is re-initialized if config changes (e.g. after login)
      this.configService.config$
        .pipe(filter((c): c is AppConfig => !!c && !!c.firebase))
        .subscribe(() => {
          this.initMessaging();
          this.listenMessages();
        });
    }
  }

  private initMessaging() {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const apps = getApps();
        if (apps.length > 0 && apps[0].options.apiKey) {
          this.messaging = getMessaging(apps[0]);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('MessageService: Failed to init messaging', error);
      }
    }
  }

  public async init(): Promise<string | null> {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    // Wait for config to be ready (it will have the right Firebase keys)
    await firstValueFrom(
      this.configService.config$.pipe(
        filter((c): c is AppConfig => !!c && !!c.firebase && !!c.firebase.apiKey)
      )
    );

    // Register service worker with config parameters
    if ('serviceWorker' in navigator) {
      const config = environment.firebase;
      const queryParams = new URLSearchParams({
        apiKey: config.apiKey,
        authDomain: config.authDomain,
        projectId: config.projectId,
        storageBucket: config.storageBucket,
        messagingSenderId: config.messagingSenderId,
        appId: config.appId,
        measurementId: config.measurementId,
      }).toString();

      try {
        this.swRegistration = await navigator.serviceWorker.register(
          `/firebase-messaging-sw.js?${queryParams}`
        );
      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    }

    this.initMessaging();
    this.listenMessages();
    return await this.getPermission();
  }

  public async getPermission(): Promise<string | null> {
    if (!isPlatformBrowser(this.platformId) || !this.messaging) {
      return null;
    }
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        // console.warn('Notification permission not granted.');
        return null;
      }

      const token = await getToken(this.messaging, {
        vapidKey: environment.firebase.vapidKey,
        serviceWorkerRegistration: this.swRegistration,
      });

      if (token && isPlatformBrowser(this.platformId)) {
        localStorage.setItem('deviceToken', token);
        this.configService.updateDevice(token).subscribe();
        return token;
      } else {
        // console.warn('⚠️ No FCM token available.');
        return null;
      }
    } catch {
      // console.error('❌ Error getting FCM token:', error);
      return null;
    }
  }

  private listenMessages() {
    if (!this.messaging || !isPlatformBrowser(this.platformId)) {
      return;
    }
    onMessage(this.messaging, (payload) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.handleNotification(payload as any);
      this.playNotificationSound();
    });
  }

  handleNotification(payload: NotificationPayload) {
    const notif = payload.notification || {};
    this.notificationTitle = notif.title ? notif.title : '🔔 New Message';
    this.notificationBody = notif.body ? notif.body : 'You have a new notification.';
    this.orderId = payload.data.order_id;
    this.routePath = `main/orders-tab`;
    if (this.notificationTitle && this.notificationBody && this.routePath) {
      this.showTooltip(this.notificationTitle, this.notificationBody, this.routePath);
    }
  }

  playAudioFor30Seconds() {
    if (!this.audio || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.audio.load();
    this.audio.muted = false;
    this.audio.loop = true;
    this.audio.play();
  }

  playNotificationSound() {
    this.playAudioFor30Seconds();
    setTimeout(() => {
      this.stopAudio();
    }, 2000);
  }

  stopAudio() {
    if (this.audio) {
      this.audio.muted = true;
    }
  }

  initiateAudio() {
    if (!this.audio || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.audio.load();
    // Browsers block autoplay before user interaction; catch and ignore the error
    this.audio.play().catch(() => {
      // Silently ignore – audio will work once the user interacts with the page
    });
    this.audio.muted = true;
  }

  showTooltip(title: string, body: string, route = '') {
    this.title = title;
    this.body = body;
    this.route = route;
    const toastRef = this.toastr.success(this.body, '', {
      timeOut: 3000,
      enableHtml: true,
      progressBar: true,
      closeButton: true,
    });
    if (toastRef && toastRef.onTap) {
      toastRef.onTap.subscribe(() => this.onToastClick());
    }
    const notifAction: NotificationAction = {
      orderId: null,
      path: route,
      new: true,
    };

    clearTimeout(this.timeoutId);
    this.timeoutId = setTimeout(() => {
      this.show = false;
      this.notification$.next(notifAction);
    }, 3000);
  }

  onToastClick() {
    const notifAction: NotificationAction = {
      orderId: this.orderId,
      path: `${this.route}/${this.orderId}`,
      new: true,
    };
    if (!this.router.url.includes('orders')) {
      this.router.navigate([notifAction.path]);
      this.closeToast();
    } else {
      this.closeToast();
      this.notification$.next(notifAction);
    }
  }

  closeToast() {
    this.show = false;
    clearTimeout(this.timeoutId);
  }
}
